import { type FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from './manageapp.module.scss'

import { Col, Row } from 'antd'
import { format, isSameMinute, parse } from 'date-fns'
import { Form, Formik } from 'formik'

import { Button } from 'common/components/Button/Button'
import { FormControl } from 'common/components/FormItems/FormControl/FormControl'
import { Modal, ModalAction } from 'common/components/Modal'
import PhoneInput from 'common/components/PhoneInput/PhoneInput'
import { Show } from 'common/components/Show/Show'
import {
  BUTTON_CONSTANTS,
  BUTTON_MODIFIER,
  BUTTON_PROPORTION,
  BUTTON_SEVERITY,
} from 'common/constants/buttonConstants'
import { FORM_CONTROL_TYPE } from 'common/constants/formControlConstants'
import { INPUT_TYPES } from 'common/constants/inputConstants'
import { MODAL_LABELS } from 'common/constants/modalConstants'
import { TIME_FORMAT } from 'common/constants/timeFormat'
import type { IOption } from 'common/interfaces/IOption'

import {
  ACTION_TYPES,
  COUNT_AS_OVERLAPPING,
  INFO_CONSTANTS,
  PATIENT_STATUSES,
  ROOM_STATUS,
} from 'features/Home/constants/infoConstants'
import {
  INITIAL_PATIENT_DATA,
  NEW_PATIENT_FORM_KEYS,
} from 'features/Home/constants/infoNewPatientFormKeys'
import { NEW_PATIENT_LABELS } from 'features/Home/constants/infoNewPatientLabels'
import { ROOM_TYPE_IDS } from 'features/Home/constants/room.constants'
import type { IPatientConsultationStaff, IPatientRoom } from 'features/Home/interfaces/IInfoPatient'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { NEW_PATIENT_SCHEMA } from 'features/Home/schemas/infoNewPatientSchema'
import { InfoService } from 'features/Home/services/infoService'
import { PATIENTS_LABELS } from 'features/Patients/constants/patientsConstants'
import type { IPatient } from 'features/Patients/interfaces/IPatient'

import { useDetectMobile } from 'app/hooks/useDetectMobile'
import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { useApiResponse } from 'common/hooks/useApiResponse'
import type {
  ApiResponse,
  ITriggerRequest,
  ITriggerResponse,
} from 'common/interfaces/IRequestResponse'
import { DateService } from 'common/services/dateService'
import dayjs from 'dayjs'
import 'features/Home/Book/Booking/Schedule/PatientSchedule/Calendar/time-picker.css'
import { Warning } from 'features/Home/Book/Booking/Schedule/PatientSchedule/Calendar/Warning/Warning'
import {
  PatientFormModal,
  type PatientFormType,
} from 'features/Patients/components/PatientFormModal/PatientFormModal'
import { useCreatePatientMutation } from 'features/Patients/state/api/patientApi'
import { type ScheduleAppointmentDto } from 'features/Schedule/api/schedule.types'
import { useScheduleActions } from 'features/Schedule/pages/components/ScheduleDayView/hooks/useScheduleActions'
import { useScheduleWeekActions } from 'features/Schedule/pages/components/ScheduleWeekView/hooks/useScheduleWeekActions'
import { EScheduleView } from 'features/Schedule/pages/Schedule'
import { PatientSearchModal } from './PatientSearchModal/PatientSearchModal'
import { type ManageAppointmentProps } from './types'
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog'
const { preserveDateInTimezone } = DateService

export const ManageAppointment: FC<ManageAppointmentProps> = memo(
  ({
    scheduleView,
    open,
    isEditing,
    editedPatientData,
    prePopulateSchedule,
    title = INFO_CONSTANTS.ADD_APPOINTMENT,
    setOpen,
    disabledEverything = false,
    customAppointment,
    date: day,
    languages,
    consultation_staffs,
    rooms: roomsData,
    schedules,
    disabledIntervals,
    dayInterval,
    timeZone,
    appointmentCategories,
    byPassTemperatureCheck,
    weekDays,
  }) => {
    const [isAddPatientOpen, setIsAddPatientOpen] = useState(false)

    const [appointmentTimeOpen, setAppointmentTimeOpen] = useState<boolean>(false)
    const [isNotifyOpen, setNotifyOpen] = useState<boolean>(false)
    const [isLoadingDelete, setIsLoadingDelete] = useState(false)
    const [isManualCheckInOpen, setManualCheckInOpen] = useState<boolean>(false)
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState<boolean>(false)
    const [autoCompletedPatient, setAutoCompletedPatient] = useState<IPatient>(null)
    const [selectedRoom, setSelectedRoom] = useState<IPatientRoom>(null)
    const [overlappingEvents, setOverlappingEvents] = useState<number>(0)
    const [overlappingDisabledInterval, setOverlappingDisabledInterval] = useState<boolean>(false)
    const [patientAccessCode, setPatientAccessCode] = useState<number | string | null>(null)
    const [awaitConfirmationSchedule, setAwaitConfirmationSchedule] = useState<any>(0)
    const [disabledUntil, setDisabledUntil] = useState(
      parseInt(localStorage.getItem(`patient-${editedPatientData?.id}-disabledUntil`) || '0', 10),
    )
    const [isPatientSearchOpen, setIsPatientSearchOpen] = useState<boolean>(false)

    const formRef = useRef(null)
    const resetCountryFunction = useRef<() => void | null>(null)

    const { setNotification } = useNotification()
    const isMobile = useDetectMobile()

    const formattedDate = dayjs(day).format('YYYY-MM-DD')
    const isFuture = useMemo(() => {
      const selectedDateInTz = preserveDateInTimezone(formattedDate, timeZone)
      const nowInTz = preserveDateInTimezone(dayjs(), timeZone)
      return selectedDateInTz.isAfter(nowInTz, 'day')
    }, [formattedDate, timeZone])

    const scheduleActions =
      scheduleView === EScheduleView.WEEK ? useScheduleWeekActions() : useScheduleActions()
    const {
      createAppointment,
      updateAppointment,
      notifyPatient,
      deleteAppointment,
      checkInPatient,
      isLoading,
    } = scheduleActions

    const { processApiResponse } = useApiResponse()
    const [createPatient]: ITriggerRequest<ITriggerResponse> = useCreatePatientMutation()

    useEffect(() => {
      if (isEditing) {
        setSelectedRoom(editedPatientData?.room)
      }
      if (title === INFO_CONSTANTS.ADD_WALK_IN_PATIENT) {
        resetCountryFunction.current?.()
      }
      if (customAppointment) {
        const [label, value] = customAppointment.room.split('/')
        const room = roomsData.find((room: any) => room.label === label && room.value === value)
        setSelectedRoom(room)
      }
    }, [isEditing, customAppointment, title])

    useEffect(() => {
      const selectedSchedule = schedules.find(
        (schedule: IPatientSchedule) => schedule.id === editedPatientData?.id,
      )

      if (selectedSchedule && selectedSchedule?.access_code) {
        setPatientAccessCode(selectedSchedule?.access_code)
      }
    }, [schedules])

    // update at the start of the minute
    useEffect(() => {
      const now = new Date()
      const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds()
      const initialTimeout = setTimeout(() => {
        runCheckAndUpdateStartTime()
        // update every minute since the start of the minute
        const interval = setInterval(() => {
          runCheckAndUpdateStartTime()
        }, 60000)

        return () => {
          clearInterval(interval)
        }
      }, msUntilNextMinute)

      return () => {
        clearTimeout(initialTimeout)
      }
    }, [appointmentTimeOpen, open, isEditing, editedPatientData])

    useEffect(() => {
      if (editedPatientData) {
        setPatientAccessCode(editedPatientData.access_code)

        const storedDisabledUntil = parseInt(
          localStorage.getItem(`patient-${editedPatientData.id}-disabledUntil`) || '0',
          10,
        )
        setDisabledUntil(storedDisabledUntil)
      }
    }, [editedPatientData])

    useEffect(() => {
      if (disabledUntil > Date.now()) {
        const remainingTime = disabledUntil - Date.now()
        const timer = setTimeout(() => {
          setDisabledUntil(0)
        }, remainingTime)

        return () => clearTimeout(timer)
      }
    }, [disabledUntil])

    useEffect(() => {
      if (autoCompletedPatient && formRef.current) {
        formRef.current.setValues(
          {
            ...formRef.current.values,
            [NEW_PATIENT_FORM_KEYS.NAME]: autoCompletedPatient.name,
            [NEW_PATIENT_FORM_KEYS.ID]: autoCompletedPatient.id,
            [NEW_PATIENT_FORM_KEYS.FIRST_NAME]: autoCompletedPatient.first_name,
            [NEW_PATIENT_FORM_KEYS.LAST_NAME]: autoCompletedPatient.last_name,
            [NEW_PATIENT_FORM_KEYS.PHONE]: autoCompletedPatient.phone,
            [NEW_PATIENT_FORM_KEYS.LANGUAGE]: autoCompletedPatient.language_type_code,
            [NEW_PATIENT_FORM_KEYS.LANGUAGE_TYPE_CODE]: autoCompletedPatient.language_type_code,
          },
          true,
        )
      }
    }, [autoCompletedPatient])

    const displayDeletePatientTrash = useMemo(() => isEditing, [isEditing])

    const displayCancelAppointmentButton = useMemo(
      () =>
        isEditing &&
        !disabledEverything &&
        editedPatientData?.status?.code !== PATIENT_STATUSES.NO_SHOW &&
        InfoService.checkIfActionShouldBeVisible(
          ACTION_TYPES.CANCEL,
          editedPatientData?.status?.code,
        ),
      [isEditing, disabledEverything, editedPatientData],
    )

    const displayCheckOutButton = useMemo(
      () =>
        isEditing &&
        !disabledEverything &&
        InfoService.checkIfActionShouldBeVisible(
          ACTION_TYPES.CHECK_OUT,
          editedPatientData?.status?.code,
        ),
      [isEditing, disabledEverything, editedPatientData],
    )

    const enableBypassTempCheck = useMemo(
      () =>
        !disabledEverything &&
        InfoService.checkIfActionShouldBeVisible(
          ACTION_TYPES.EDIT_TEMPERATURE,
          editedPatientData?.status?.code,
        ),
      [editedPatientData, selectedRoom, customAppointment],
    )

    const enableChangeRoom = useMemo(
      () =>
        !disabledEverything &&
        InfoService.checkIfActionShouldBeVisible(
          ACTION_TYPES.RESCHEDULE,
          editedPatientData?.status?.code,
        ),
      [editedPatientData, selectedRoom, customAppointment, disabledEverything],
    )

    const enableChangeConsultationStaff = useMemo(
      () =>
        !disabledEverything &&
        InfoService.checkIfActionShouldBeVisible(
          ACTION_TYPES.EDIT_CONSULTATION_STAFF,
          editedPatientData?.status?.code,
        ),
      [editedPatientData, customAppointment, disabledEverything],
    )

    const updateOverlappingSlots = (room: IPatientRoom, values: any) => {
      if (!room) return
      const [start, end] = values

      const isInsideDisabledInterval = disabledIntervals.some((disabledInterval: any) =>
        InfoService.checkIsOverlappingIntervals(
          disabledInterval,
          start,
          end,
          `${room.label}/${room.value}`,
        ),
      )
      setOverlappingDisabledInterval(isInsideDisabledInterval)
      if (isInsideDisabledInterval) {
        return
      }

      const eventsFromRoom = schedules
        .filter(
          (schedule: IPatientSchedule): boolean =>
            schedule.room?.value === room.value &&
            schedule.id !== editedPatientData?.id &&
            COUNT_AS_OVERLAPPING.includes(schedule.status.code),
        )
        .map((item: IPatientSchedule) => ({
          id: item.id,
          title: item.patient.name,
          start: new Date(`${day} ${item.start_time}`),
          end: new Date(`${day} ${item.end_time}`),
          resourceId: item?.room?.label,
          status: item.status,
          temperature_bypass: item.temperature_bypass,
          isDraggable: true,
          current_location: item.current_location,
          appointment_start_time: item.appointment_start_time,
          appointment_end_time: item.appointment_end_time,
          original_room: item.original_room,
          room_type_id: item.room.room_type_id,
          procedure: item.procedure,
        }))

      const { nrOfOverlappingEvents } = InfoService.getNumberOfOverlappingIntervals(
        { start, end, resourceId: room.label },
        eventsFromRoom,
        room.label,
      )
      setOverlappingEvents(nrOfOverlappingEvents)
      return nrOfOverlappingEvents
    }

    const enableChangeAppointmentTime = useMemo(
      () =>
        selectedRoom &&
        !disabledEverything &&
        InfoService.checkIfActionShouldBeVisible(
          ACTION_TYPES.RESIZE_SCHEDULE,
          editedPatientData?.status?.code,
        ),
      [selectedRoom, disabledEverything, editedPatientData],
    )

    const onFieldChange = ({
      field,
      fieldValue,
      setFieldValue,
      values,
      setErrors,
      errors,
    }: {
      field: string
      fieldValue: any
      setFieldValue: Function
      values?: any
      option?: any
      setErrors?: any
      errors?: any
    }): void => {
      if (field === 'first_name' || field === 'last_name') {
        if (values) updateOverlappingSlots(selectedRoom, values?.appointment)

        return
      }

      if (field === 'language') {
        setFieldValue(NEW_PATIENT_FORM_KEYS.LANGUAGE_TYPE_CODE, fieldValue)
      }

      if (field === 'category_type_code') {
        setFieldValue(NEW_PATIENT_FORM_KEYS.CATEGORY_TYPE_CODE, fieldValue)
      }

      if (field === 'consultation_staff') {
        setFieldValue(NEW_PATIENT_FORM_KEYS.CONSULTATION_STAFF, fieldValue)
      }

      if (field === 'room') {
        const room: IPatientRoom | undefined = roomsData.find(
          (room: IPatientRoom): boolean => room.value === fieldValue,
        )
        setSelectedRoom(room)
        updateOverlappingSlots(room, values.appointment)
        // getPreCompleteValues(room, setFieldValue)

        const updatedErrors = { ...errors }
        delete updatedErrors[NEW_PATIENT_FORM_KEYS.APPOINTMENT]
        setErrors({ ...updatedErrors })
      }

      if (field === 'appointment') {
        updateOverlappingSlots(selectedRoom, fieldValue)
        const [_, selectedEndTime] = fieldValue

        // Get slots based on view type (day vs week)
        const getSlotsForDay = () => {
          if (scheduleView === EScheduleView.WEEK && weekDays) {
            const formattedDate = dayjs(day).format('YYYY-MM-DD')
            const daySlots = weekDays[formattedDate]?.time_slots || []
            return daySlots.map((slot) => ({
              ...slot,
              start_time: slot.start_time,
              end_time: slot.end_time,
            }))
          }
          return selectedRoom?.slots || []
        }

        const daySlots = getSlotsForDay()
        const sortedSlots = InfoService.getSortedSlots(daySlots, day)
        const endOfTheSchedule = sortedSlots.at(-1)?.end_time

        const isAfterSchedule =
          endOfTheSchedule && dayjs(selectedEndTime).isAfter(endOfTheSchedule, 'minutes')

        if (isAfterSchedule) {
          formRef?.current?.setFieldError(
            NEW_PATIENT_FORM_KEYS.APPOINTMENT,
            INFO_CONSTANTS.TIME_OUTSIDE_SCHEDULE,
          )
        }
      }
    }

    const clearModalData = () => {
      setOpen()
      setSelectedRoom(null)
      setAutoCompletedPatient(null)
      setOverlappingEvents(0)
      setOverlappingDisabledInterval(false)
      document.querySelectorAll('.e-selected-cell')?.forEach((el) => {
        el.classList.remove('e-selected-cell')
      })
    }

    const handleCreateAppointment = async (data: any, resetForm: Function) => {
      if (isLoading) return

      const {
        id,
        room,
        appointment,
        temperature_bypass,
        first_name,
        last_name,
        phone,
        language_type_code,
        consultation_staff,
        procedure,
        category_type_code,
        title,
      } = data
      const propPatient = {
        id,
        first_name,
        last_name,
        phone,
        language_type_code,
      }

      let patient = autoCompletedPatient ?? propPatient
      if (
        autoCompletedPatient &&
        propPatient.language_type_code &&
        autoCompletedPatient.language_type_code !== propPatient.language_type_code
      ) {
        patient = {
          ...autoCompletedPatient,
          language_type_code: propPatient.language_type_code,
        }
      }

      const adjustedData = {
        room,
        patient,
        consultation_staff,
        temperature_bypass: temperature_bypass ? 1 : 0,
        start_time: format(appointment.at(0), TIME_FORMAT.TIME_PICKER_HH_SS),
        end_time: format(appointment.at(1), TIME_FORMAT.TIME_PICKER_HH_SS),
        procedure_code: !procedure?.isCustom ? procedure?.code : null,
        procedure_description: procedure?.description,
        category_type_code,
        title,
      } as ScheduleAppointmentDto

      const searchRoom: IPatientRoom | undefined = roomsData.find(
        (patientRoom: IPatientRoom): boolean => patientRoom.value === room,
      )

      const nrOverlappingEvents = updateOverlappingSlots(searchRoom, appointment)

      const closeAndReset = () => {
        clearModalData()
        resetForm()
      }

      const handleCreatePatient = async () => {
        await createAppointment(adjustedData, getDateParam())
        closeAndReset()
      }

      if (nrOverlappingEvents > 1) {
        const awaitConfirmationData = {
          scheduleData: adjustedData,
          resetFormCallback: closeAndReset,
          confirmAction: handleCreatePatient,
        }
        setAwaitConfirmationSchedule(awaitConfirmationData)
      } else {
        handleCreatePatient()
      }
    }

    const timePickerOptions = useMemo(() => {
      if (!selectedRoom) {
        return []
      }

      // Get slots based on view type (day vs week)
      const getSlotsForDay = () => {
        // Week view: get slots from the specific day
        if (scheduleView === EScheduleView.WEEK && weekDays) {
          const formattedDate = dayjs(day).format('YYYY-MM-DD')
          const daySlots = weekDays[formattedDate]?.time_slots || []
          return daySlots.map((slot) => ({
            ...slot,
            start_time: slot.start_time,
            end_time: slot.end_time,
          }))
        }

        // Day view: use room slots
        return selectedRoom?.slots || []
      }

      const daySlots = getSlotsForDay()
      const sortedSlots = InfoService.getSortedSlots(daySlots, day)

      if (sortedSlots.length === 0) {
        return []
      }

      const start_time = sortedSlots[0].start_time
      const end_time = sortedSlots.at(-1).end_time

      return [{ start_time, end_time }]
    }, [schedules, selectedRoom, day, editedPatientData, scheduleView, weekDays])

    const proceedToUpdateAppointment = async (scheduleData: any, resetForm: Function) => {
      await updateAppointment(scheduleData, getDateParam(), editedPatientData?.id)
      clearModalData()
      resetForm()
    }

    const handleNotify = async () => {
      await notifyPatient(editedPatientData?.id)
      setNotifyOpen(false)
      const newDisabledUntil = Date.now() + 60 * 1000
      localStorage.setItem(
        `patient-${editedPatientData?.id}-disabledUntil`,
        newDisabledUntil.toString(),
      )
      setDisabledUntil(newDisabledUntil)
    }

    const handleUpdateAppointment = async (data: any, resetForm: Function) => {
      if (isLoading) return

      const {
        room,
        appointment,
        consultation_staff,
        temperature_bypass,
        procedure,
        first_name,
        last_name,
        phone,
        language_type_code,
        category_type_code,
        title,
      } = data

      const [start_time, end_time] = appointment

      // Construct patient object from form data
      const patient = {
        id: editedPatientData?.patient?.id,
        first_name,
        last_name,
        phone,
        language_type_code,
      }

      const adjustedData = {
        room,
        patient,
        consultation_staff,
        temperature_bypass: temperature_bypass ? 1 : 0,
        start_time: format(start_time, TIME_FORMAT.TIME_PICKER_HH_SS),
        end_time: format(end_time, TIME_FORMAT.TIME_PICKER_HH_SS),
        procedure_code: !procedure?.isCustom ? procedure?.code : null,
        procedure_description: procedure?.description,
        category_type_code,
        title,
      }

      if (overlappingEvents > 1) {
        const awaitConfirmationData = {
          scheduleData: adjustedData,
          resetFormCallback: resetForm,
          confirmAction: proceedToUpdateAppointment,
        }
        setAwaitConfirmationSchedule(awaitConfirmationData)
        return
      }

      proceedToUpdateAppointment(adjustedData, resetForm)
    }

    const handleConfirmUpdateAppointment = async () => {
      const { scheduleData, resetFormCallback, confirmAction } = awaitConfirmationSchedule
      confirmAction(scheduleData, resetFormCallback)
      setAwaitConfirmationSchedule(null)
    }
    const defaultLanguageName = languages.find((lang: IOption) => lang.code === 1)?.name

    const defaultConsultationStaff = consultation_staffs.find(
      (consultation_staff: IPatientConsultationStaff) => consultation_staff.is_doctor === 1,
    )

    function checkShouldUpdateTime() {
      // First check if we have current form values
      if (formRef.current?.values?.appointment?.length === 2) {
        const [currentStart, currentEnd] = formRef.current.values.appointment
        return [currentStart, currentEnd]
      }

      const startTime = new Date(`${day} ${editedPatientData?.start_time}`)
      const endTime = new Date(`${day} ${editedPatientData?.end_time}`)

      return [startTime, endTime]
    }

    const runCheckAndUpdateStartTime = () => {
      if (!open || !editedPatientData || !formRef.current) return

      const [startTime, endTime] = checkShouldUpdateTime()

      // Only update if times are different to avoid unnecessary rerenders
      const [currentStart, currentEnd] = formRef.current.values.appointment || []
      if (!isSameMinute(startTime, currentStart) || !isSameMinute(endTime, currentEnd)) {
        formRef.current.setFieldValue(NEW_PATIENT_FORM_KEYS.APPOINTMENT, [startTime, endTime])
      }
    }

    const getInitialFormValues = () => {
      if (isEditing) {
        return {
          [NEW_PATIENT_FORM_KEYS.ID]: editedPatientData?.id,
          [NEW_PATIENT_FORM_KEYS.NAME]: editedPatientData?.patient.name,
          [NEW_PATIENT_FORM_KEYS.FIRST_NAME]: editedPatientData?.patient.first_name,
          [NEW_PATIENT_FORM_KEYS.LAST_NAME]: editedPatientData?.patient.last_name,
          [NEW_PATIENT_FORM_KEYS.PHONE]: editedPatientData?.patient.phone,
          [NEW_PATIENT_FORM_KEYS.LANGUAGE]: editedPatientData?.patient.language,
          [NEW_PATIENT_FORM_KEYS.ROOM]:
            typeof editedPatientData?.room === 'string'
              ? editedPatientData?.room
              : editedPatientData?.room.value,
          [NEW_PATIENT_FORM_KEYS.APPOINTMENT]: [
            editedPatientData?.start_time instanceof Date
              ? editedPatientData?.start_time
              : new Date(`${day} ${editedPatientData?.start_time}`),
            editedPatientData?.end_time instanceof Date
              ? editedPatientData?.end_time
              : new Date(`${day} ${editedPatientData?.end_time}`),
          ],
          [NEW_PATIENT_FORM_KEYS.CONSULTATION_STAFF]: editedPatientData?.consultation_staff,
          [NEW_PATIENT_FORM_KEYS.TEMPERATURE_BYPASS]: Boolean(
            editedPatientData?.temperature_bypass,
          ),
          [NEW_PATIENT_FORM_KEYS.PROCEDURE]: editedPatientData?.procedure,
          [NEW_PATIENT_FORM_KEYS.CATEGORY_TYPE_CODE]: editedPatientData?.category?.code,
          [NEW_PATIENT_FORM_KEYS.TITLE]: editedPatientData?.title,
        }
      } else if (!isEditing && !customAppointment) {
        return {
          ...INITIAL_PATIENT_DATA,
          [NEW_PATIENT_FORM_KEYS.LANGUAGE]: defaultLanguageName,
          [NEW_PATIENT_FORM_KEYS.CONSULTATION_STAFF]: defaultConsultationStaff?.id,
          [NEW_PATIENT_FORM_KEYS.PROCEDURE]: defaultConsultationStaff?.procedure,
          [NEW_PATIENT_FORM_KEYS.CATEGORY_TYPE_CODE]: appointmentCategories?.[1]?.code || '',
          [NEW_PATIENT_FORM_KEYS.TITLE]: appointmentCategories?.[1]?.name || '',
          [NEW_PATIENT_FORM_KEYS.TEMPERATURE_BYPASS]: byPassTemperatureCheck,
        }
      }

      if (customAppointment) {
        const [label, value] = customAppointment.room.split('/')
        const room = roomsData.find((room: any) => room.label === label && room.value === value)
        return {
          ...INITIAL_PATIENT_DATA,
          [NEW_PATIENT_FORM_KEYS.APPOINTMENT]: customAppointment?.appointment,
          [NEW_PATIENT_FORM_KEYS.ROOM]: room?.value,
          [NEW_PATIENT_FORM_KEYS.LANGUAGE]: defaultLanguageName,
          [NEW_PATIENT_FORM_KEYS.CONSULTATION_STAFF]: defaultConsultationStaff?.id,
          [NEW_PATIENT_FORM_KEYS.PROCEDURE]: defaultConsultationStaff?.procedure,
          [NEW_PATIENT_FORM_KEYS.CATEGORY_TYPE_CODE]: appointmentCategories?.[1]?.code || '',
          [NEW_PATIENT_FORM_KEYS.TITLE]: appointmentCategories?.[1]?.name || '',
          [NEW_PATIENT_FORM_KEYS.TEMPERATURE_BYPASS]: byPassTemperatureCheck,
        }
      }

      return INITIAL_PATIENT_DATA
    }

    const formatTimeWithCheck = (timeValue: string | Date) => {
      if (typeof timeValue === 'string') {
        const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/
        if (timeRegex.test(timeValue)) {
          return timeValue
        }
      }

      return dayjs(timeValue).tz(timeZone).format('HH:mm:ss')
    }

    const handleUpdatePatientStatus = async (status: PATIENT_STATUSES, updatedRoom?: string) => {
      if (isLoading) return

      const { first_name, last_name, phone, language_type_code } = formRef.current.values

      const patient = {
        id: editedPatientData?.patient?.id,
        first_name,
        last_name,
        phone,
        language_type_code,
      }

      const patientData = {
        room:
          updatedRoom ||
          (typeof editedPatientData.room === 'string'
            ? editedPatientData.room
            : editedPatientData.room?.value),
        patient,
        start_time: formatTimeWithCheck(editedPatientData.start_time),
        end_time: formatTimeWithCheck(editedPatientData.end_time),
        temperature_bypass: editedPatientData.temperature_bypass,
        consultation_staff: editedPatientData.consultation_staff,
        status_type_code: status,
        procedure_code: editedPatientData?.procedure?.code,
        procedure_description: editedPatientData?.procedure?.description,
        category_type_code: editedPatientData?.category?.code,
        title: editedPatientData?.title,
      }

      await updateAppointment(patientData, getDateParam(), editedPatientData?.id)
      clearModalData()
      setOpen()
    }

    const handleCancelAppointment = () => {
      handleUpdatePatientStatus(PATIENT_STATUSES.CANCELED)
      clearModalData()
    }

    const handleManualCheckInPatient = () => {
      checkInPatient(editedPatientData?.id, editedPatientData?.access_code!, getDateParam())
      setManualCheckInOpen(false)
    }

    const handleCheckOut = () => {
      const { room } = formRef.current.values
      handleUpdatePatientStatus(PATIENT_STATUSES.CHECKED_OUT, room)
    }

    const getAppointmentTime = () => {
      if (editedPatientData?.room?.room_type_id !== ROOM_TYPE_IDS.WAITING_ROOM) return
      const { appointment_start_time, appointment_end_time, original_room } = editedPatientData
      if (!appointment_start_time || !appointment_end_time) return ''
      const startDate = parse(appointment_start_time, 'HH:mm:ss', new Date())
      const endDate = parse(appointment_end_time, 'HH:mm:ss', new Date())

      const start = format(startDate, TIME_FORMAT.TIME_GUTTER_PICKER_HH_SS)
      const end = format(endDate, TIME_FORMAT.TIME_GUTTER_PICKER_HH_SS)
      return `Original appointment: (${start} - ${end}, ${original_room})`
    }

    const handleRemoveSchedule = useCallback(
      async (sendSms: boolean) => {
        setIsLoadingDelete(true)
        const sendSmsValue = sendSms ? 1 : 0
        await deleteAppointment(getDateParam(), editedPatientData?.id, sendSmsValue)
          .then((response: any) => {
            setIsLoadingDelete(false)
            setNotification({
              title: 'Delete Success',
              description: response.message,
              type: ALERT_CONSTANTS.SUCCESS,
            })
          })
          .catch(() => {
            setIsLoadingDelete(false)
          })
        setIsDeleteConfirmOpen(false)
        clearModalData()
        setOpen()
      },
      [editedPatientData, day, deleteAppointment],
    )

    const handleShowDeleteConfirmation = () => {
      setIsDeleteConfirmOpen(true)
    }

    const handleDeleteConfirm = async (sendSms: boolean) => {
      await handleRemoveSchedule(sendSms)
    }

    const handleDeleteCancel = () => {
      setIsDeleteConfirmOpen(false)
    }

    const getRoomErrorMessage = () => {
      if (!selectedRoom) return null

      const shouldApplyRoomRule = !InfoService.checkIfCurrentDateOverlapDisabledIntervals(
        disabledIntervals,
        selectedRoom,
        dayInterval,
        timeZone,
      )
      const excludedRooms = [ROOM_STATUS.FREE, ROOM_STATUS.CLEANUP, ROOM_STATUS.BUSY]

      const shouldDisplayRoomWarning =
        selectedRoom &&
        selectedRoom?.status &&
        !excludedRooms.includes(selectedRoom?.status?.code) &&
        shouldApplyRoomRule

      if (shouldDisplayRoomWarning) {
        return INFO_CONSTANTS.BUSY_ROOM_WARNING
      }
      if (overlappingEvents > selectedRoom?.max_patients && !selectedRoom?.is_waiting_room) {
        return `You reached the max number of ${
          selectedRoom?.max_patients || 1
        } patients at the same time for this room.`
      }
    }

    const appointmentOriginalTime = getAppointmentTime()

    function preSubmit() {
      // const [startTime] = data.appointment
      const { setFieldError } = formRef.current
      if (overlappingDisabledInterval) {
        setFieldError('appointment', INFO_CONSTANTS.DISABLED_INTERVAL_WARNING)
        return
      }
      onSubmit()
    }

    function onSubmit() {
      const { values, resetForm } = formRef.current
      const data = { ...values }

      if (isEditing) {
        handleUpdateAppointment(data, resetForm)
      } else {
        handleCreateAppointment(data, resetForm)
      }
    }

    const handlePatientSelect = useCallback((patient: IPatient) => {
      if (patient && formRef.current) {
        setAutoCompletedPatient(patient)
        const updatedValues = {
          ...formRef.current.values,
          [NEW_PATIENT_FORM_KEYS.NAME]: patient.name,
          [NEW_PATIENT_FORM_KEYS.ID]: patient.id,
          [NEW_PATIENT_FORM_KEYS.FIRST_NAME]: patient.first_name,
          [NEW_PATIENT_FORM_KEYS.LAST_NAME]: patient.last_name,
          [NEW_PATIENT_FORM_KEYS.PHONE]: patient.phone,
          [NEW_PATIENT_FORM_KEYS.LANGUAGE]: patient.language_type_code,
          [NEW_PATIENT_FORM_KEYS.LANGUAGE_TYPE_CODE]: patient.language_type_code,
        }

        formRef.current.setValues(updatedValues, true)

        const errorsToClear = [
          NEW_PATIENT_FORM_KEYS.FIRST_NAME,
          NEW_PATIENT_FORM_KEYS.LAST_NAME,
          NEW_PATIENT_FORM_KEYS.PHONE,
          NEW_PATIENT_FORM_KEYS.LANGUAGE,
        ]

        const currentErrors = formRef.current.errors || {}
        const updatedErrors = { ...currentErrors }

        errorsToClear.forEach((field) => {
          delete updatedErrors[field]
        })

        formRef.current.setErrors(updatedErrors)
      }
      setIsPatientSearchOpen(false)
    }, [])

    const handleSubmitPatientForm = useCallback(
      async (patient: PatientFormType) => {
        const { first_name, last_name, phone, language_type_code, doctor_emr_id } = patient
        const response: ApiResponse = await createPatient({
          first_name,
          last_name,
          phone,
          language_type_code,
          doctor_emr_id,
        })

        processApiResponse(response, {
          success: 'Create patient success',
          error: 'Create patient error',
        })

        if (response?.data?.patient) {
          handlePatientSelect(response.data.patient)
          setIsAddPatientOpen(false)
        }
      },
      [createPatient, handlePatientSelect],
    )

    const handleCloseFormModal = useCallback(() => {
      setIsAddPatientOpen(false)
    }, [])

    const getDateParam = () => {
      return dayjs(day).format('YYYY-MM-DD')
    }

    return (
      <>
        <PatientFormModal
          isOpen={isAddPatientOpen}
          onClose={handleCloseFormModal}
          onSubmit={handleSubmitPatientForm}
          languages={languages}
        />
        <div className={styles.parent}>
          <Formik
            innerRef={formRef}
            initialValues={getInitialFormValues()}
            onSubmit={preSubmit}
            enableReinitialize
            validationSchema={NEW_PATIENT_SCHEMA}
            validateOnBlur
            validateOnChange={false}>
            {({
              setFieldValue,
              resetForm,
              setFieldError,
              values,
              errors,
              setErrors,
              validateForm,
            }) => {
              const roomErrorMessage = getRoomErrorMessage()
              return (
                <Modal
                  footer={null}
                  classNames={{ content: styles.modalContainer }}
                  title={
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                      }}>
                      <span>{title}</span>
                    </div>
                  }
                  destroyOnHidden
                  open={open && !Boolean(awaitConfirmationSchedule)}
                  keyboard
                  cancelButton
                  onCancel={() => {
                    clearModalData()
                    resetForm()
                  }}>
                  <Form className={styles.parentForm}>
                    <Show when={(values as any)?.id && !isEditing}>
                      <div className={styles.patientButtons}>
                        <Button
                          className={styles.changePatient}
                          modifier={BUTTON_MODIFIER.SECONDARY}
                          proportion={BUTTON_PROPORTION.SMALL}
                          onClick={() => setIsPatientSearchOpen(true)}>
                          Change patient
                        </Button>
                        {!isEditing && (
                          <Button
                            modifier={BUTTON_MODIFIER.PRIMARY}
                            proportion={BUTTON_PROPORTION.SMALL}
                            onClick={() => setIsAddPatientOpen(true)}>
                            New patient
                          </Button>
                        )}
                      </div>
                    </Show>
                    <Show when={!(values as any)?.id}>
                      <Row gutter={16} align='bottom'>
                        <Col xs={isMobile && (values as any)?.phone ? 24 : isMobile ? 16 : 12}>
                          <FormControl
                            required
                            label='Patient'
                            type={INPUT_TYPES.TEXT}
                            name='patient_selector'
                            control={FORM_CONTROL_TYPE.INPUT}
                            placeholder='Click to select'
                            readOnly
                            onClick={() => setIsPatientSearchOpen(true)}
                            className={styles.patientSelector}
                          />
                        </Col>
                        {!isMobile && (
                          <Show when={(values as any)?.phone}>
                            <Col xs={12}>
                              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                                <div style={{ flex: 1 }}>
                                  <FormControl
                                    disabled
                                    value={(values as any)?.phone}
                                    label='Cell Phone'
                                    name='phone_display'
                                    control={FORM_CONTROL_TYPE.INPUT}
                                    readOnly
                                  />
                                </div>
                                <Button
                                  onClick={() => setIsAddPatientOpen(true)}
                                  className={styles.parentCompactButton}>
                                  New patient
                                </Button>
                              </div>
                            </Col>
                          </Show>
                        )}
                        {!isMobile && !(values as any)?.phone && (
                          <Col>
                            <Button
                              onClick={() => setIsAddPatientOpen(true)}
                              className={styles.parentCompactButton}>
                              New patient
                            </Button>
                          </Col>
                        )}
                        {/* Mobile: New patient button in same row when no phone */}
                        {isMobile && !(values as any)?.phone && (
                          <Col xs={8}>
                            <Button
                              onClick={() => setIsAddPatientOpen(true)}
                              className={styles.parentCompactButton}>
                              New patient
                            </Button>
                          </Col>
                        )}
                        <Show when={!!errors?.phone}>
                          <Col xs={24}>
                            <p className='errorMessage'>Please select a patient</p>
                          </Col>
                        </Show>
                      </Row>

                      {/* Mobile layout: Phone on separate row when phone exists */}
                      {isMobile && (values as any)?.phone && (
                        <Row gutter={16} align='bottom'>
                          <Col xs={24}>
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                              <div style={{ flex: 1 }}>
                                <FormControl
                                  disabled
                                  value={(values as any)?.phone}
                                  label='Cell Phone'
                                  name='phone_display'
                                  control={FORM_CONTROL_TYPE.INPUT}
                                  readOnly
                                />
                              </div>
                              <Button
                                onClick={() => setIsAddPatientOpen(true)}
                                className={styles.parentCompactButton}>
                                New patient
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      )}
                    </Show>
                    <Show when={(values as any)?.id}>
                      <Row gutter={[16, 16]} align='top'>
                        <Col xs={24} md={12}>
                          <FormControl
                            required
                            label={NEW_PATIENT_LABELS.FIRST_NAME}
                            type={INPUT_TYPES.TEXT}
                            name={NEW_PATIENT_FORM_KEYS.FIRST_NAME}
                            control={FORM_CONTROL_TYPE.INPUT}
                            disabled
                            tabIndex={1}
                            onChange={(e: any, option: any) => {
                              const value = e.target.value
                              setFieldValue(
                                NEW_PATIENT_FORM_KEYS.FIRST_NAME,
                                value.replace(/\s+/g, ' '),
                              )
                              const updatedErrors = { ...errors }
                              delete (updatedErrors as any).first_name
                              setErrors({ ...updatedErrors })

                              onFieldChange({
                                field: NEW_PATIENT_FORM_KEYS.FIRST_NAME,
                                fieldValue: value,
                                setFieldValue,
                                option,
                                setErrors,
                                errors,
                                values,
                              })
                            }}
                          />
                        </Col>
                        <Col xs={24} md={12}>
                          <FormControl
                            required
                            label={NEW_PATIENT_LABELS.LAST_NAME}
                            type={INPUT_TYPES.TEXT}
                            name={NEW_PATIENT_FORM_KEYS.LAST_NAME}
                            control={FORM_CONTROL_TYPE.INPUT}
                            disabled
                            tabIndex={2}
                            onChange={(value: any, option: any) => {
                              setFieldValue(
                                NEW_PATIENT_FORM_KEYS.LAST_NAME,
                                value.replace(/\s+/g, ' '),
                              )
                              const updatedErrors = { ...errors }
                              delete (updatedErrors as any).last_name
                              setErrors({ ...updatedErrors })

                              onFieldChange({
                                field: NEW_PATIENT_FORM_KEYS.LAST_NAME,
                                fieldValue: value,
                                setFieldValue,
                                option,
                                setErrors,
                                errors,
                                values,
                              })
                            }}
                          />
                        </Col>
                      </Row>

                      <Row gutter={[16, 16]} align='top'>
                        <Col xs={24} md={12}>
                          <div className={styles.parentSelect}>
                            <PhoneInput
                              required
                              error={errors?.phone as string}
                              value={values?.phone as string}
                              onBlur={() => validateForm()}
                              onChange={(value) =>
                                setFieldValue(NEW_PATIENT_FORM_KEYS.PHONE, value)
                              }
                              onResetCountry={(func) => (resetCountryFunction.current = func)}
                              disabled
                              tabIndex={3}
                              label={
                                <span>
                                  {NEW_PATIENT_LABELS.PHONE}
                                  <span className={styles.parentFormDescriptionPhone}>
                                    {' '}
                                    (Must Receive Texts)
                                  </span>
                                </span>
                              }
                            />
                          </div>
                        </Col>
                        <Col xs={24} md={12}>
                          <FormControl
                            label={NEW_PATIENT_LABELS.LANGUAGE}
                            type={INPUT_TYPES.TEXT}
                            name={NEW_PATIENT_FORM_KEYS.LANGUAGE}
                            control={FORM_CONTROL_TYPE.SELECT}
                            disabled
                            tabIndex={4}
                            options={languages.map((language: IOption) => ({
                              code: language.code,
                              name: language.name,
                            }))}
                            onChange={(value) => {
                              const updatedErrors = { ...errors }
                              delete updatedErrors.language
                              setErrors({ ...updatedErrors })

                              onFieldChange({
                                field: NEW_PATIENT_FORM_KEYS.LANGUAGE,
                                fieldValue: value,
                                setFieldValue,
                                values,
                                setErrors,
                                errors,
                              })
                            }}
                          />
                        </Col>
                      </Row>
                    </Show>

                    <Row gutter={[16, 16]} align='top'>
                      <Col xs={24} md={12}>
                        <FormControl
                          required
                          label={NEW_PATIENT_LABELS.CATEGORY}
                          type={INPUT_TYPES.TEXT}
                          name={NEW_PATIENT_FORM_KEYS.CATEGORY_TYPE_CODE}
                          control={FORM_CONTROL_TYPE.SELECT}
                          disabled={disabledEverything}
                          options={appointmentCategories?.map((category) => ({
                            name: category.name,
                            code: category.code,
                          }))}
                          onChange={(value) => {
                            const updatedErrors = { ...errors }
                            delete updatedErrors.category_type_code
                            setErrors({ ...updatedErrors })

                            onFieldChange({
                              field: NEW_PATIENT_FORM_KEYS.CATEGORY_TYPE_CODE,
                              fieldValue: value,
                              setFieldValue,
                              values,
                              setErrors,
                              errors,
                            })
                          }}
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <FormControl
                          label={NEW_PATIENT_LABELS.TITLE}
                          type={INPUT_TYPES.TEXT}
                          name={NEW_PATIENT_FORM_KEYS.TITLE}
                          control={FORM_CONTROL_TYPE.INPUT}
                          disabled={disabledEverything}
                          onChange={(e: any) => {
                            const value = e.target.value
                            setFieldValue(NEW_PATIENT_FORM_KEYS.TITLE, value)
                            const updatedErrors = { ...errors }
                            delete updatedErrors.title
                            setErrors({ ...updatedErrors })

                            onFieldChange({
                              field: NEW_PATIENT_FORM_KEYS.TITLE,
                              fieldValue: value,
                              setFieldValue,
                              values,
                              setErrors,
                              errors,
                            })
                          }}
                        />
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]} align='top'>
                      <Col xs={24} md={12}>
                        <FormControl
                          required
                          label={NEW_PATIENT_LABELS.ROOM}
                          type={INPUT_TYPES.TEXT}
                          name={NEW_PATIENT_FORM_KEYS.ROOM}
                          control={FORM_CONTROL_TYPE.SELECT}
                          disabled={!enableChangeRoom}
                          options={roomsData.map((room: any) => ({
                            name: room.label,
                            code: room.value,
                          }))}
                          onChange={(value) => {
                            const updatedErrors = { ...errors }
                            delete updatedErrors.room
                            setErrors({ ...updatedErrors })

                            onFieldChange({
                              field: NEW_PATIENT_FORM_KEYS.ROOM,
                              fieldValue: value,
                              setFieldValue,
                              values,
                              setErrors,
                              errors,
                            })
                          }}
                        />
                        {roomErrorMessage && (
                          <p className={styles.parentRoomError}>{roomErrorMessage}</p>
                        )}
                      </Col>
                      <Col xs={24} md={12}>
                        <FormControl
                          required
                          step={prePopulateSchedule}
                          label={NEW_PATIENT_LABELS.APPOINTMENT_TIME}
                          timezone={timeZone}
                          type={INPUT_TYPES.TEXT}
                          name={NEW_PATIENT_FORM_KEYS.APPOINTMENT}
                          control={FORM_CONTROL_TYPE.TIME_PICKER_SLOTS}
                          options={timePickerOptions}
                          disabled={!enableChangeAppointmentTime}
                          setFieldError={setFieldError}
                          onFocus={() => setAppointmentTimeOpen(true)}
                          onHide={() => setAppointmentTimeOpen(false)}
                          onChange={(value) => {
                            const updatedErrors = { ...errors }
                            delete updatedErrors.appointment
                            setErrors({ ...updatedErrors })

                            onFieldChange({
                              field: NEW_PATIENT_FORM_KEYS.APPOINTMENT,
                              fieldValue: value,
                              setFieldValue,
                              values,
                            })
                          }}
                          date={day}
                        />
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]} align='top'>
                      <Col xs={24} md={12}>
                        <div className={styles.parentSelect}>
                          <FormControl
                            label={NEW_PATIENT_LABELS.APPOINTMENT_WITH}
                            type={INPUT_TYPES.TEXT}
                            name={NEW_PATIENT_FORM_KEYS.CONSULTATION_STAFF}
                            disabled={!enableChangeConsultationStaff || disabledEverything}
                            control={FORM_CONTROL_TYPE.SELECT}
                            options={consultation_staffs
                              ?.filter((user) => user.is_provider || user.is_doctor)
                              .map((consultation_staff: IPatientConsultationStaff) => ({
                                code: consultation_staff.id,
                                name: consultation_staff.name,
                              }))}
                            onChange={(value) => {
                              const updatedErrors = { ...errors }
                              delete updatedErrors.consultation_staff
                              setErrors({ ...updatedErrors })

                              onFieldChange({
                                field: NEW_PATIENT_FORM_KEYS.CONSULTATION_STAFF,
                                fieldValue: value,
                                setFieldValue,
                                values,
                                setErrors,
                                errors,
                              })
                            }}
                          />
                        </div>
                      </Col>
                      <Col xs={24} md={12}>
                        <div className={styles.parentTemperatureCheck}>
                          <FormControl
                            label={NEW_PATIENT_LABELS.TEMPERATURE_BYPASS}
                            type={INPUT_TYPES.CHECKBOX}
                            name={NEW_PATIENT_FORM_KEYS.TEMPERATURE_BYPASS}
                            control={FORM_CONTROL_TYPE.CHECKBOX}
                            disabled={!enableBypassTempCheck || disabledEverything}
                          />
                        </div>
                      </Col>
                    </Row>
                    {!!editedPatientData && !!patientAccessCode && (
                      <>
                        <div className={styles.parentBottomActions}>
                          <p className={styles.parentPatientCode}>
                            {INFO_CONSTANTS.PATIENT_CODE}:{' '}
                            <span>{editedPatientData?.access_code}</span>
                          </p>

                          {editedPatientData?.status.code !== PATIENT_STATUSES.CHECKED_OUT && (
                            <>
                              <Button
                                className={styles.parentNotifyButton}
                                modifier={BUTTON_MODIFIER.SECONDARY}
                                proportion={BUTTON_PROPORTION.SMALL}
                                onClick={() => setNotifyOpen(true)}
                                disabled={isLoading || Date.now() < disabledUntil}>
                                {BUTTON_CONSTANTS.NOTIFY_PATIENT}
                              </Button>
                              {!isFuture && (
                                <Button
                                  className={styles.parentManualCheckInButton}
                                  modifier={BUTTON_MODIFIER.SECONDARY}
                                  proportion={BUTTON_PROPORTION.SMALL}
                                  onClick={() => setManualCheckInOpen(true)}
                                  disabled={isLoading}>
                                  {BUTTON_CONSTANTS.MANUAL_CHECK_IN}
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                        {editedPatientData?.last_notification && (
                          <p className={styles.parentLastSMS}>
                            {INFO_CONSTANTS.LAST_SMS_SENT}{' '}
                            <span>
                              {format(
                                parse(
                                  editedPatientData.last_notification.replace(
                                    'Last Text Message sent on ',
                                    '',
                                  ),
                                  `MM/dd/yyyy 'at' ${TIME_FORMAT.TWELVE_HOUR_FORMAT_WITH_SECONDS}`,
                                  new Date(),
                                ),
                                `MM/dd/yyyy 'at' ${TIME_FORMAT.TWELVE_HOUR_FORMAT}`,
                              )}
                            </span>
                          </p>
                        )}
                        {editedPatientData?.error_sms_message && (
                          <p className={styles.parentErrorMessage}>
                            {INFO_CONSTANTS.ERROR}{' '}
                            <span>{editedPatientData?.error_sms_message}</span>
                          </p>
                        )}
                      </>
                    )}
                    <Show when={appointmentOriginalTime}>
                      <p className={styles.parentLastSMS}>{appointmentOriginalTime}</p>
                    </Show>
                    <div className={styles.parentFormActions} style={{ marginTop: '8px' }}>
                      <div className={styles.parentFormSubmitLeft}>
                        {displayCancelAppointmentButton && (
                          <Button
                            className={styles.parentSubmitButton}
                            modifier={BUTTON_MODIFIER.SECONDARY}
                            onClick={handleCancelAppointment}
                            disabled={isLoading}>
                            Cancel Appointment
                          </Button>
                        )}
                      </div>

                      <div className={styles.parentFormSubmitRight}>
                        {displayCheckOutButton && (
                          <Button
                            className={styles.parentSubmitButton}
                            severity={BUTTON_SEVERITY.SUCCESS_FILLED}
                            modifier={BUTTON_MODIFIER.PRIMARY}
                            onClick={handleCheckOut}
                            disabled={isLoading}>
                            {BUTTON_CONSTANTS.CHECK_OUT}
                          </Button>
                        )}

                        {!disabledEverything &&
                          editedPatientData?.status?.code !== PATIENT_STATUSES.CHECKED_OUT && (
                            <Button
                              className={styles.parentSubmitButton}
                              htmlType='submit'
                              data-cy='submit-btn'
                              loading={isLoading}
                              disabled={Boolean(roomErrorMessage) || !!errors?.appointment}
                              severity={BUTTON_SEVERITY.SUCCESS_FILLED}>
                              {BUTTON_CONSTANTS.SAVE}
                            </Button>
                          )}

                        {displayDeletePatientTrash && (
                          <Button
                            className={styles.parentSubmitButton}
                            severity={BUTTON_SEVERITY.DANGER}
                            modifier={BUTTON_MODIFIER.PRIMARY}
                            onClick={handleShowDeleteConfirmation}
                            disabled={isLoading || isLoadingDelete}>
                            {BUTTON_CONSTANTS.DELETE}
                          </Button>
                        )}

                        <Button
                          className={styles.parentSubmitButton}
                          data-cy='cancel-btn'
                          loading={isLoading}
                          onClick={() => {
                            clearModalData()
                            resetForm()
                          }}>
                          {BUTTON_CONSTANTS.CANCEL}
                        </Button>
                      </div>
                    </div>
                  </Form>
                </Modal>
              )
            }}
          </Formik>
          <Warning
            open={Boolean(awaitConfirmationSchedule)}
            title={INFO_CONSTANTS.ARE_YOU_SURE}
            warning={INFO_CONSTANTS.MULTIPLE_PATIENTS_TITLE}
            okButtonText={INFO_CONSTANTS.YES}
            handleClose={() => setAwaitConfirmationSchedule(null)}
            handleConfirm={handleConfirmUpdateAppointment}
            hasCancel
          />

          <ModalAction
            destroyOnHidden
            isOpen={isNotifyOpen}
            title={MODAL_LABELS.NOTIFY_PATIENT}
            confirmText={BUTTON_CONSTANTS.SEND}
            cancelText={BUTTON_CONSTANTS.CANCEL}
            onConfirm={() => {
              setNotifyOpen(false)
              handleNotify()
            }}
            onClose={() => setNotifyOpen(false)}
            description={PATIENTS_LABELS.CONFIRM_NOTIFY}
            confirmButtonSeverity={BUTTON_SEVERITY.SUCCESS_FILLED}
          />

          {!isFuture && (
            <ModalAction
              destroyOnHidden
              isOpen={isManualCheckInOpen}
              title={MODAL_LABELS.MANUAL_CHECK_IN}
              confirmText={BUTTON_CONSTANTS.YES}
              cancelText={BUTTON_CONSTANTS.CANCEL}
              onConfirm={() => {
                setManualCheckInOpen(false)
                handleManualCheckInPatient()
              }}
              onClose={() => setManualCheckInOpen(false)}
              description={PATIENTS_LABELS.CONFIRM_MANUAL_CHECK_IN}
              confirmButtonSeverity={BUTTON_SEVERITY.SUCCESS_FILLED}
            />
          )}
          {isPatientSearchOpen && (
            <PatientSearchModal
              isOpen={isPatientSearchOpen}
              onClose={() => setIsPatientSearchOpen(false)}
              onSelectPatient={handlePatientSelect}
            />
          )}
          <DeleteConfirmationDialog
            isOpen={isDeleteConfirmOpen}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            isLoading={isLoadingDelete}
          />
        </div>
      </>
    )
  },
)
